import React, { useCallback, useEffect, useRef, useState } from "react";
import io from "socket.io-client";
import {
  Alert, Avatar, Button, Card, Col, Grid, Modal,
  Row, Space, Spin, Steps, Switch, Tag, Typography, message,
} from "antd";
import {
  CheckCircleOutlined, ClockCircleOutlined, DisconnectOutlined,
  QrcodeOutlined, ReloadOutlined, SafetyCertificateOutlined, WhatsAppOutlined,
} from "@ant-design/icons";
import { API_BASE, SOCKET_URL } from "../../config/api";
import { getAuthHeaders, getAuthToken } from "../../utils/auth";

const { Title, Text, Paragraph } = Typography;
const STATUS = {
  idle: { label: "Sin vincular", color: "default", title: "Conectá el WhatsApp del salón", description: "Vinculá una cuenta para ofrecer atención automática a tus clientes." },
  starting: { label: "Iniciando", color: "processing", title: "Preparando la conexión", description: "Estamos iniciando WhatsApp y buscando una sesión guardada." },
  qr: { label: "Esperando escaneo", color: "gold", title: "Escaneá el código QR", description: "Usá el teléfono de la cuenta de WhatsApp que querés vincular." },
  authenticated: { label: "Cuenta vinculada", color: "processing", title: "Código escaneado correctamente", description: "WhatsApp reconoció el teléfono y está terminando de conectar la cuenta." },
  ready: { label: "Conectado", color: "success", title: "Tu WhatsApp está conectado", description: "La cuenta está vinculada y lista para recibir consultas." },
  disconnected: { label: "Desconectado", color: "warning", title: "Se perdió la conexión", description: "Revisá el teléfono y la conexión a Internet. Podés volver a conectar la sesión." },
  error: { label: "Requiere atención", color: "error", title: "Revisá la conexión", description: "No se pudo completar la conexión. Consultá el detalle y volvé a intentarlo." },
};
const panelStyle = { borderRadius: 18, border: "none", boxShadow: "0 10px 32px rgba(15,52,96,.06)" };
const goldButton = { background: "#8a6d3b", borderColor: "#8a6d3b", borderRadius: 10 };

export default function WhatsAppQR() {
  const screens = Grid.useBreakpoint();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [realtime, setRealtime] = useState(false);
  const [error, setError] = useState("");
  const [confirmAction, setConfirmAction] = useState("");
  const [connectionDelayed, setConnectionDelayed] = useState(false);
  const loadingStatus = useRef(false);

  const acceptStatus = useCallback((next) => {
    setData((previous) => previous && new Date(previous.updatedAt) > new Date(next.updatedAt) ? previous : next);
  }, []);
  const load = useCallback(async (silent = false) => {
    if (loadingStatus.current) return;
    loadingStatus.current = true;
    if (!silent) setLoading(true);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(`${API_BASE}/whatsapp/status`, { headers: getAuthHeaders(), cache: "no-store", signal: controller.signal });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "No se pudo consultar el estado");
      acceptStatus(result);
      setError("");
    } catch (requestError) { setError(requestError.message || "No se pudo conectar con el servidor"); }
    finally { window.clearTimeout(timeout); loadingStatus.current = false; setLoading(false); }
  }, [acceptStatus]);

  useEffect(() => {
    load();
    const socket = io(`${SOCKET_URL}/whatsapp`, { auth: { token: getAuthToken() }, reconnection: true });
    socket.on("connect", () => { setRealtime(true); load(true); });
    socket.on("disconnect", () => setRealtime(false));
    socket.on("connect_error", () => setRealtime(false));
    socket.on("whatsapp:status", acceptStatus);
    return () => socket.disconnect();
  }, [acceptStatus, load]);

  // Poll while linking, so a missed socket event cannot leave the QR hidden.
  useEffect(() => {
    if (data?.status === "ready" && realtime) return undefined;
    const timer = window.setInterval(() => load(true), 2500);
    return () => window.clearInterval(timer);
  }, [data?.status, realtime, load]);

  const run = async (name, payload, method = "POST") => {
    if (busy) return;
    setBusy(name);
    try {
      const response = await fetch(`${API_BASE}/whatsapp/${name}`, {
        method, headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(payload || {}),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "No se pudo completar la acción");
      acceptStatus(result);
      setConfirmAction("");
      if (result.status === "error") message.warning(result.error || "Revisá el estado del bot");
      else message.success(name === "automation" ? (result.enabled ? "Respuestas activadas" : "Respuestas pausadas") : name === "logout" ? "Cuenta desvinculada" : "Conexión iniciada");
    } catch (actionError) { message.error(actionError.message || "No se pudo completar la acción"); }
    finally { setBusy(""); }
  };

  const current = STATUS[data?.status] || STATUS.idle;
  const ready = data?.status === "ready";
  const starting = ["starting", "authenticated"].includes(data?.status);
  useEffect(() => {
    setConnectionDelayed(false);
    if (!starting) return undefined;
    const timer = window.setTimeout(() => setConnectionDelayed(true), 30000);
    return () => window.clearTimeout(timer);
  }, [starting]);
  // QR validity is controlled by the server, not the computer clock.
  const qrVisible = data?.status === "qr" && Boolean(data.qr);

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: screens.md ? 24 : 0 }}>
      <div style={{ marginBottom: 24 }}>
        <Row gutter={[18, 18]} justify="space-between" align="middle">
          <Col span={24}>
            <Text style={{ color: "#1da851", fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>Canal de atención</Text>
            <Title level={2} style={{ margin: "4px 0 6px", color: "#17233d", fontSize: screens.md ? 30 : 24 }}>Conectar WhatsApp</Title>
            <Text type="secondary">Vincula el número del salón para responder consultas, iniciar reservas y enviar recordatorios.</Text>
          </Col>
        </Row>
      </div>

      {error && <Alert showIcon type="error" message="No pudimos consultar el estado" description={error} action={<Button onClick={() => load()}>Reintentar</Button>} style={{ marginBottom: 18 }} />}
      {!realtime && data && <Alert showIcon type="info" message="Consultando la conexión automáticamente" description="Mientras se recupera la conexión en vivo, el estado y el QR se consultan cada pocos segundos." style={{ marginBottom: 18 }} />}

      {loading && !data ? <div style={{ padding: 70, textAlign: "center" }}><Spin size="large" /></div> : data && (
        <>
          <Row gutter={[20, 20]}>
            <Col xs={24} lg={15}>
              <Card style={{ ...panelStyle, minHeight: 480 }} styles={{ body: { padding: screens.md ? 24 : 18 } }}>
                <Row justify="space-between" align="middle" gutter={[10, 10]}>
                  <Col><Space size={12}><Avatar shape="square" size={46} icon={<WhatsAppOutlined />} style={{ borderRadius: 14, background: "#e9fbef", color: "#1da851", fontSize: 24 }} /><div><Text strong style={{ display: "block", fontSize: 16 }}>Estado del dispositivo</Text><Text type="secondary">Sesión segura de WhatsApp Web</Text></div></Space></Col>
                  <Col><Space size={16} wrap>
                    <Space size={8}><Text>{data.enabled ? "Activado" : "Desactivado"}</Text><Switch aria-label="Activar respuestas automáticas" checked={data.enabled} checkedChildren="ON" unCheckedChildren="OFF" loading={busy === "automation"} disabled={Boolean(busy)} onChange={(enabled) => run("automation", { enabled }, "PATCH")} /></Space>
                    <Tag color={current.color} icon={ready ? <CheckCircleOutlined /> : starting ? <ReloadOutlined spin /> : <QrcodeOutlined />} style={{ padding: "6px 11px", borderRadius: 999, margin: 0 }}>{current.label}</Tag>
                  </Space></Col>
                </Row>
                <Alert showIcon type={ready && data.enabled ? "success" : data.status === "error" ? "error" : "info"} message={ready ? (data.enabled ? "El bot está activo y puede responder mensajes." : "Las respuestas automáticas están pausadas. Los avisos de citas conservan su configuración.") : qrVisible ? "Escanea el código desde WhatsApp para vincular el dispositivo." : current.description} style={{ borderRadius: 11, marginTop: 20, marginBottom: 24 }} />
                <div style={{ minHeight: 300, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                  {qrVisible ? (
                    <>
                      <div style={{ boxSizing: "border-box", background: "#fff", padding: 16, borderRadius: 18, border: "1px solid #e9ecef", boxShadow: "0 8px 25px rgba(0,0,0,.08)", width: "100%", maxWidth: 322 }}>
                        <img src={data.qr} alt="Código QR para vincular WhatsApp del salón" style={{ width: "100%", height: "auto", display: "block" }} />
                      </div>
                      <Text type="secondary" style={{ display: "block", marginTop: 15 }}>El código se renueva automáticamente cuando vence.</Text>
                    </>
                  ) : (
                    <>
                      {starting ? <Spin size="large" /> : ready ? <CheckCircleOutlined style={{ fontSize: 76, color: "#25D366" }} /> : <QrcodeOutlined style={{ fontSize: 70, color: "#8c8c8c" }} />}
                      <Title level={3} style={{ margin: "18px 0 5px" }}>{ready ? "WhatsApp conectado" : current.title}</Title>
                      <Paragraph type="secondary" style={{ maxWidth: 430, margin: 0 }}>{ready ? "Estudio Isaac ya puede atender mensajes y enviar notificaciones." : data.status === "authenticated" ? "El teléfono ya fue vinculado. Esta pantalla se actualizará automáticamente al terminar la conexión." : data.status === "qr" ? "Esperando un código nuevo. Si tarda demasiado, usá Reconectar." : current.description}</Paragraph>
                      {ready && <Button danger icon={<DisconnectOutlined />} loading={busy === "logout"} disabled={Boolean(busy)} onClick={() => setConfirmAction("logout")} style={{ marginTop: 22 }}>Desconectar WhatsApp</Button>}
                      {connectionDelayed && <Text type="secondary" style={{ marginTop: 14 }}>La conexión está tardando. Puedes volver a generar el código.</Text>}
                      {!ready && !qrVisible && <Button type="primary" style={{ ...goldButton, marginTop: 22 }} icon={<QrcodeOutlined />} disabled={Boolean(busy) || (starting && !connectionDelayed)} loading={busy === "start" || busy === "restart"} onClick={() => run(data.status === "idle" ? "start" : "restart")}>
                        Generar código QR
                      </Button>}
                    </>
                  )}
                </div>
                {data.error && <Alert showIcon type={data.status === "error" ? "error" : "warning"} message={data.error} style={{ marginBottom: 18 }} />}
                {data.retryAt && <Paragraph type="secondary"><ClockCircleOutlined /> Próximo intento automático: {new Date(data.retryAt).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}</Paragraph>}
              </Card>
            </Col>
            <Col xs={24} lg={9}>
              <Card title="Cómo vincularlo" style={panelStyle} styles={{ body: { padding: screens.md ? 24 : 18 } }}>
                <Steps direction="vertical" current={ready ? 3 : data.status === "authenticated" ? 2 : qrVisible ? 1 : 0} items={[
                  { title: "Abre WhatsApp", description: "En el teléfono que utilizará el salón." },
                  { title: "Dispositivos vinculados", description: "Entra en Configuración y selecciona Vincular dispositivo." },
                  { title: "Escanea el código", description: "Apunta la cámara al QR que aparece en esta pantalla." },
                  { title: "Conexión lista", description: "El estado cambiará automáticamente a Conectado." },
                ]} />
              </Card>
              <Card style={{ ...panelStyle, marginTop: 20, background: "#f7fafc" }} styles={{ body: { padding: 24 } }}>
                <Space align="start"><SafetyCertificateOutlined style={{ color: "#1677ff", fontSize: 20, marginTop: 3 }} /><div><Text strong>Recomendación de seguridad</Text><Text type="secondary" style={{ display: "block", marginTop: 4 }}>Vincula únicamente el teléfono oficial del salón. No compartas capturas del código QR.</Text></div></Space>
                <Button block onClick={() => load()} icon={<ReloadOutlined />} loading={loading} disabled={Boolean(busy)} style={{ marginTop: 18 }}>Actualizar estado</Button>
              </Card>
            </Col>
          </Row>


        </>
      )}
      <Modal open={Boolean(confirmAction)} title={confirmAction === "logout" ? "¿Desvincular la cuenta?" : "¿Reconectar WhatsApp?"} onCancel={() => !busy && setConfirmAction("")} onOk={() => run(confirmAction)} confirmLoading={Boolean(busy)} closable={!busy} maskClosable={!busy} cancelButtonProps={{ disabled: Boolean(busy) }} okText={confirmAction === "logout" ? "Desvincular" : "Reconectar"} cancelText="Cancelar" okButtonProps={{ danger: confirmAction === "logout" }}>
        <Paragraph>{confirmAction === "logout" ? "Se cerrará la sesión de WhatsApp. Para volver a vincular la cuenta tendrás que escanear un nuevo QR." : "La atención automática se interrumpirá mientras se restablece la conexión. Se intentará recuperar la sesión guardada."}</Paragraph>
      </Modal>

    </div>
  );
}

